
import { useState } from "react";
import { ChevronDown, Filter } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface Participant {
  ageGroup: string;
  bmiGroup: string;
  tags: string[];
}

interface FilterBarProps {
  participants?: Participant[];
  onFiltersChange?: (filters: string[]) => void;
}

const FilterBar = ({ participants = [], onFiltersChange }: FilterBarProps) => {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  
  const addFilter = (filter: string) => {
    if (!activeFilters.includes(filter)) {
      const newFilters = [...activeFilters, filter];
      setActiveFilters(newFilters);
      onFiltersChange?.(newFilters);
    }
  };
  
  const removeFilter = (filter: string) => {
    const newFilters = activeFilters.filter((f) => f !== filter);
    setActiveFilters(newFilters);
    onFiltersChange?.(newFilters);
  };

  const clearAllFilters = () => {
    setActiveFilters([]);
    onFiltersChange?.([]);
  };

  // Calculate counts for filter options
  const getAgeGroupCount = (ageGroup: string) => {
    return participants.filter(p => p.ageGroup === ageGroup).length;
  };

  const getBmiGroupCount = (bmiGroup: string) => {
    return participants.filter(p => p.bmiGroup === bmiGroup).length;
  };

  const getTagCount = (tag: string) => {
    return participants.filter(p => p.tags.includes(tag)).length;
  };
  
  return (
    <div className="flex flex-col space-y-4">
      <div className="flex flex-wrap items-center gap-2 sm:gap-4">
        <div className="flex items-center">
          <Filter className="h-4 w-4 mr-2" />
          <span className="text-sm font-medium">Filters:</span>
        </div>
        
        <Select onValueChange={(value) => addFilter(value)}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="Demographics" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="18-24">18-24 ({getAgeGroupCount("18-24")})</SelectItem>
            <SelectItem value="25-34">25-34 ({getAgeGroupCount("25-34")})</SelectItem>
            <SelectItem value="35-44">35-44 ({getAgeGroupCount("35-44")})</SelectItem>
            <SelectItem value="45-54">45-54 ({getAgeGroupCount("45-54")})</SelectItem>
            <SelectItem value="55-64">55-64 ({getAgeGroupCount("55-64")})</SelectItem>
            <SelectItem value="65+">65+ ({getAgeGroupCount("65+")})</SelectItem>
            <SelectItem value="Normal">BMI: Normal ({getBmiGroupCount("Normal")})</SelectItem>
            <SelectItem value="Overweight">BMI: Overweight ({getBmiGroupCount("Overweight")})</SelectItem>
            <SelectItem value="Underweight">BMI: Underweight ({getBmiGroupCount("Underweight")})</SelectItem>
            <SelectItem value="Obese">BMI: Obese ({getBmiGroupCount("Obese")})</SelectItem>
            <SelectItem value="Diabetic">Diabetic ({getTagCount("Diabetic")})</SelectItem>
            <SelectItem value="Non-diabetic">Non-diabetic ({getTagCount("Non-diabetic")})</SelectItem>
          </SelectContent>
        </Select>
        
        <Select onValueChange={(value) => addFilter(value)}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="Time Range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Last 7 days">Last 7 days</SelectItem>
            <SelectItem value="Last 30 days">Last 30 days</SelectItem>
            <SelectItem value="Last 90 days">Last 90 days</SelectItem>
            <SelectItem value="Last Year">Last Year</SelectItem>
          </SelectContent>
        </Select>
        
        <Select onValueChange={(value) => addFilter(value)}>
          <SelectTrigger className="w-[140px] h-9">
            <SelectValue placeholder="Diet Tags" />
          </SelectTrigger>
          <SelectContent>
            {/* Show unique diet tags from participants */}
            {Array.from(new Set(participants.flatMap(p => p.tags.filter(tag => 
              !['Diabetic', 'Non-diabetic'].includes(tag)
            )))).sort().map(tag => (
              <SelectItem key={tag} value={tag}>
                {tag} ({getTagCount(tag)})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={clearAllFilters}
        >
          Clear All
        </Button>
      </div>
      
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeFilters.map((filter) => (
            <Badge key={filter} variant="secondary" className="px-2 py-1">
              {filter}
              <button
                onClick={() => removeFilter(filter)}
                className="ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center hover:bg-muted"
              >
                ✕
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterBar;
